/** Talks to the grown-ups API. Every call fails softly when the device is offline. */

export interface ConsentSummary {
  childFirstName: string;
  childLastName: string;
  version: string;
  consentedAt: string;
}

export interface ParentStatus {
  signedIn: boolean;
  email?: string;
  parentFirstName?: string | null;
  parentLastName?: string | null;
  consentVersion: string;
  consent?: ConsentSummary | null;
}

export interface ConsentForm {
  parentFirstName: string;
  parentLastName: string;
  birthYear: number;
  childFirstName: string;
  childLastName: string;
  agreed: boolean;
  consentVersion: string;
}

export class ParentApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'ParentApiError';
  }
}

async function parseError(response: Response, fallback: string): Promise<never> {
  let message = fallback;
  try {
    const body = (await response.json()) as { error?: string };
    if (body.error) message = body.error;
  } catch {
    // keep fallback
  }
  throw new ParentApiError(message, response.status);
}

export async function fetchParentStatus(): Promise<ParentStatus> {
  const response = await fetch('/api/me', { credentials: 'same-origin' });
  if (!response.ok) await parseError(response, 'The grown-ups area is not reachable right now.');
  return (await response.json()) as ParentStatus;
}

export async function requestLoginCode(email: string): Promise<void> {
  const response = await fetch('/api/auth/request-code', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!response.ok) await parseError(response, 'The sign-in email could not be sent.');
}

export async function verifyLoginCode(email: string, code: string): Promise<void> {
  const response = await fetch('/api/auth/verify-code', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  if (!response.ok) await parseError(response, 'That code did not work. Please try again.');
}

export async function logout(): Promise<void> {
  await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
}

export async function submitConsent(form: ConsentForm): Promise<void> {
  const response = await fetch('/api/consent', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(form),
  });
  if (!response.ok) await parseError(response, 'The consent could not be saved.');
}

export async function revokeConsent(): Promise<void> {
  const response = await fetch('/api/consent', { method: 'DELETE', credentials: 'same-origin' });
  if (!response.ok) await parseError(response, 'That did not work. Please try again.');
}

/** True when the signed-in grown-up has an active consent, so the kid UI may show the eraser. */
export async function isEraserEnabled(): Promise<boolean> {
  try {
    const status = await fetchParentStatus();
    return Boolean(status.signedIn && status.consent);
  } catch {
    return false;
  }
}
